<template>
    <div>
        <label class="d-block">Zusätzliche Dateianhänge</label>
        <csp-base-file-upload
            ref="fileUpload"
            :auto-process-queue="true"
            class="multi-file-upload"
            file-input-name="fileUpload"
            :multiple-upload="true"
            :title="$t('record document upload')"
            :upload-error-message="$t('record document upload error')"
            :upload-url="uploadUrl"
            @after-files-added="handleAfterFilesAdded"
            @before-upload="handleBeforeUpload"
            @file-added="handleFileAdded"
            @file-upload-complete="handleFileUploadComplete"
            @file-upload-progress="handleFileUpdate"
            @total-upload-progress="handleTotalUploadProgress"
            @upload-complete="handleUploadComplete"
            @upload-disabled="handleUploadDisabled">
            <slot/>
        </csp-base-file-upload>

        <b-table v-if="commonAttachments.length > 0" ref="commonAttachments" :fields="attachmentFields"
            :items="commonAttachments">
            <template v-slot:cell(selected)="rowData">
                <b-form-checkbox :checked="rowData.item.checked"
                    :value="rowData.item.name"
                    @change="(checked) => handleCheckboxChange(rowData, checked)"/>
            </template>
        </b-table>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import Form from '@form/Form';
    import '@desktop/scss/components/record/document-upload-modal.scss';

    const container = Container.getInstance();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'CommonAttachments',
        props: {
            attachments: {
                type: Array,
                required: true,
            },
            form: {
                type: Object,
                required: true,
            },
            path: {
                type: String,
                required: true,
            },
        },
        emits: ['upload-success'],
        data() {
            const fileUploadForm = new Form({
                categoryGroupId: null,
                categoryId: 16,
            });
            return {
                fileUploadForm: fileUploadForm,
                uploadNotificationSessionId: this.generateUploadNotificationSessionId(),
                queueIsProcessing: false,
                filesForUpload: [],
                totalUploadBytes: 0,
                totalBytesSent: 0,
                totalUploadProgress: 0,
                previewTableFields: [
                    {
                        key: 'categoryGroupName',
                        label: 'Zuordnung',
                        width: '50px',
                    },
                    {
                        key: 'categoryName',
                        label: 'Kategorie',
                        width: '50px',
                    },
                    {
                        key: 'fileName',
                        label: 'Dateiname',
                        width: '50px',
                    },
                    {
                        key: 'actions',
                        label: '',
                        width: '50px',
                    },
                ],
                attachmentFields: [
                    {
                        key: 'selected',
                        label: 'Auswählen',
                        thStyle: {width: '95px'},
                    },
                    {
                        key: 'label',
                        label: 'Dokument',
                    },
                ],
                commonAttachments: [],
            }
        },
        computed: {
            uploadUrl() {
                return this.path + '/?uploadSubmit=1';
            },
        },
        methods: {
            generateUploadNotificationSessionId() {
                return 'upload-notification-' + Math.random().toString(36).substring(2, 15)
                    + Math.random().toString(36).substring(2, 15);
            },
            getFileMetaForFile(file) {
                return this.filesForUpload.find(f => f.uuid === file.upload.uuid);
            },
            processQueue() {
                this.queueIsProcessing = true;
                this.$refs.fileUpload.getDropzoneInstance().processQueue();
            },
            handleFileAdded(file) {
                const categoryId = this.fileUploadForm.getDataOfProperty('categoryId');
                const categoryGroupId = this.fileUploadForm.getDataOfProperty('categoryGroupId');

                this.filesForUpload.push({
                    uuid: file.upload.uuid,
                    file,
                    categoryId,
                    categoryName: 'TMP',
                    categoryGroupId,
                    categoryGroupName: '',
                    fileName: file.name,
                });
            },
            handleFileUpdate(file) {
                const fileWithMeta = this.getFileMetaForFile(file)
                if (!fileWithMeta) {
                    return;
                }
                // update file part of the meta data object
                fileWithMeta.file = file;
            },
            handleFileUploadComplete(file) {
                this.handleFileUpdate(file);
            },
            handleTotalUploadProgress() {
                const dropzone = this.$refs.fileUpload.getDropzoneInstance();
                // Compute the sum of all bytes sent so far, including completed uploads.
                // Dropzone stops counting "completed" files in total progress.
                let bytesSent = dropzone.files.reduce((sum, file) => {
                    return sum + (file.upload?.bytesSent || 0);
                }, 0);
                // Some browsers/servers may report a few extra bytes after compression or chunking,
                bytesSent = Math.min(bytesSent, this.totalUploadBytes);
                // Compute the percentage progress
                this.totalUploadProgress = this.totalUploadBytes
                    ? Math.round((bytesSent / this.totalUploadBytes) * 100)
                    : 0;
            },
            handleBeforeUpload(file, xhr, formData) {
                formData.append('name', file.name);
                formData.append('uploadNotificationSessionId', this.uploadNotificationSessionId);
            },
            handleUploadDisabled() {
                flashMessenger.addWarning(this.$t('record document upload error no category'));
            },
            handleUploadComplete(data, statistics) {
                data.uploadedFiles.forEach((uploadedFiles) => {
                    const uploadedFilesData = JSON.parse(uploadedFiles.xhr.response);
                    uploadedFilesData.data.attributes.files.forEach((file) => {
                        this.commonAttachments.push({
                            label: file.fileName,
                            value: 0,
                            checked: file.fileId,
                            name: file.fileId,
                        })
                    })
                })
                this.form.setData(this.commonAttachments);

                this.$emit('upload-success', data);

                let translationKey = 'record document upload success';

                if (statistics.numberOfAddedFiles > 1) {
                    translationKey = translationKey + ' multiple';
                }

                if (statistics.numberOfSuccessfullyUploadedFiles === statistics.numberOfAddedFiles) {
                    flashMessenger.addSuccess(this.$t(translationKey, statistics))
                    this.reset();
                    return;
                }

                flashMessenger.addWarning(this.$t(translationKey, statistics));
            },
            removeFile(file) {
                this.$refs.fileUpload.getDropzoneInstance().removeFile(file);
            },
            reset() {
                this.uploadNotificationSessionId = this.generateUploadNotificationSessionId();
                this.totalUploadProgress = 0;
                this.queueIsProcessing = false;
                this.$refs.fileUpload.reset();
            },
            handleAfterFilesAdded(files) {
                this.totalUploadBytes = Array.from(files).reduce((sum, file) => sum + file.size, 0);
            },
            handleCheckboxChange(rowData, checked) {
                if (checked) {
                    rowData.item.checked = rowData.item.name;
                } else {
                    rowData.item.checked = false;
                }
                this.form.setData(this.commonAttachments);
            },
        },
    }
</script>
